/**
 * Drafts for one candidate on one role, in the panel the design file
 * specifies. One component serves two actions, because the mockup uses one
 * panel for all of them:
 *
 *   kind="outreach"  two drafts  - a LinkedIn note and an introductory email
 *   kind="followup"  three       - the day 3 / 7 / 14 sequence
 *
 * Layout, colours and spacing are lifted from SAKETH-KB/DESIGN rather than
 * approximated with Bootstrap.
 *
 * Nothing is sent by PINE and nothing is stored. Copy puts the selected draft
 * on the clipboard; Send hands it to the user's own mail client, or opens the
 * candidate's LinkedIn profile, because LinkedIn has no URL that pre-fills a
 * message.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import toastr from "../../../utils/toast";

const ORANGE = "#f26b2a";

/** LinkedIn's own limit on a connection-request note. */
const LINKEDIN_MAX = 300;

const KINDS = {
  outreach: {
    endpoint: "draft_outreach",
    heading: "AI Outreach Draft",
    steps: [
      "Reviewing role brief & candidate profile...",
      "Crafting personalized connection message...",
      "Generating email & LinkedIn templates...",
    ],
  },
  followup: {
    endpoint: "draft_followup",
    heading: "AI Follow-up Draft",
    steps: [
      "Checking outreach history...",
      "Assessing optimal follow-up timing...",
      "Generating multi-channel follow-up sequence...",
    ],
  },
};

const STEP_MS = 1100;

/** "12 Oct", or "12 Oct 2025" when it was not this year. */
const fmtSentDate = (value) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "earlier";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  });
};

/** How a recorded send is named in the "already sent" banner. */
const sentLabel = (s) => {
  if (s.kind === "followup") {
    return `Follow-up${s.day ? `, day ${s.day}` : ""} (${s.channel === "email" ? "email" : "LinkedIn"})`;
  }
  return s.channel === "email" ? "Introduction email" : "LinkedIn connection message";
};

/**
 * Editable fields are styled to look exactly like the text they replace, so
 * the panel reads as the design intends until you click into it.
 */
const FIELD = {
  width: "100%",
  border: "1px solid transparent",
  borderRadius: "4px",
  background: "transparent",
  padding: "2px 4px",
  margin: "0 -4px",
  font: "inherit",
  fontSize: "13px",
  color: "#222",
  outline: "none",
  resize: "none",
  display: "block",
  boxSizing: "border-box",
};

const svg = (size, children, stroke) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={stroke || "currentColor"}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const IconRobot = () =>
  svg(
    20,
    <>
      <path d="M12 8V4H8" />
      <rect width="16" height="12" x="4" y="8" rx="2" />
      <path d="M2 14h2M20 14h2M15 13v2M9 13v2" />
    </>,
    ORANGE,
  );

const IconLinkedIn = () =>
  svg(
    14,
    <>
      <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6z" />
      <rect width="4" height="12" x="2" y="9" />
      <circle cx="4" cy="4" r="2" />
    </>,
  );

const IconMail = () =>
  svg(
    14,
    <>
      <rect width="20" height="16" x="2" y="4" rx="2" />
      <path d="m22 7-10 6L2 7" />
    </>,
  );

const IconCopy = () =>
  svg(
    14,
    <>
      <rect width="14" height="14" x="8" y="8" rx="2" />
      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
    </>,
  );

const IconCheck = () => svg(14, <path d="M20 6 9 17l-5-5" />, "#22a55a");

/** A textarea that grows to fit, so a long email is not trapped behind a
 *  scrollbar inside a card. */
function AutoTextarea({ value, onChange, style }) {
  const ref = useRef(null);

  const fit = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(fit, [fit, value]);

  return (
    <textarea
      ref={ref}
      value={value}
      rows={1}
      onChange={(e) => {
        onChange(e.target.value);
        fit();
      }}
      onFocus={(e) => {
        e.target.style.borderColor = "#e0cfc2";
        e.target.style.background = "#fff";
      }}
      onBlur={(e) => {
        e.target.style.borderColor = "transparent";
        e.target.style.background = "transparent";
      }}
      style={style}
    />
  );
}

export default function OutreachDraft({
  role_id,
  candidate,
  kind = "outreach",
  onClose,
  // Told once a send is recorded, so the grid can badge the row at once.
  onSent,
}) {
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { token } = useOutletContext();

  const cfg = KINDS[kind] || KINDS.outreach;

  const [step, setStep] = useState(0);
  const [result, setResult] = useState(null);
  const [blocked, setBlocked] = useState(null);
  const [error, setError] = useState("");
  const [sel, setSel] = useState(0);
  const [copied, setCopied] = useState(false);
  const [edits, setEdits] = useState({});

  const timer = useRef(null);

  // The progress lines advance on the design's cadence but stop at the last
  // one and wait there. The request is genuinely still running; pretending it
  // had finished would be the one dishonest thing in this panel.
  useEffect(() => {
    timer.current = setInterval(() => {
      setStep((s) => (s >= cfg.steps.length - 1 ? s : s + 1));
    }, STEP_MS);
    return () => clearInterval(timer.current);
  }, [cfg.steps.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  // axios, not fetch. App.js registers a response interceptor that logs the
  // user out and redirects on 401/403, and it only sees axios traffic.
  useEffect(() => {
    let live = true;

    (async () => {
      try {
        const res = await axios.post(
          `${API_BASE_URL}/workstation/${cfg.endpoint}`,
          { role_id, candidate_id: candidate?.candidate_id },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        if (live) setResult(res.data);
      } catch (err) {
        if (!live) return;

        const status = err.response?.status;

        // The interceptor is already clearing the session and navigating away.
        if (status === 401 || status === 403) return;

        const body = err.response?.data;

        if (status === 409 && body?.blocked) setBlocked(body);
        else if (body?.error) setError(body.error);
        else setError("Could not reach PINE to write the draft.");
      }
    })();

    return () => {
      live = false;
    };
  }, [API_BASE_URL, token, role_id, candidate?.candidate_id, cfg.endpoint]);

  const person = result?.candidate || candidate || {};

  /**
   * What the user has typed over the model's draft, keyed by card index. An
   * absent key means untouched, which is different from a key holding "",
   * meaning they deliberately cleared it.
   */
  const val = (key, original) => (key in edits ? edits[key] : original);
  const edit = (key, value) => setEdits((e) => ({ ...e, [key]: value }));

  /** Both response shapes, normalised to one list of cards. */
  const msgs = (() => {
    if (!result) return [];

    const raw = result.drafts
      ? [
          {
            ch: "li",
            label: "LinkedIn Connection Message",
            meta: "LinkedIn · Connection request",
            subject: null,
            body: result.drafts.linkedin,
          },
          {
            ch: "mail",
            label: "Email",
            meta: person.email
              ? "Email · Introduction"
              : "Email · no address on record",
            subject: result.drafts.email.subject,
            body: result.drafts.email.body,
          },
        ]
      : (result.messages || []).map((m, i) => ({
          ch: m.channel === "email" ? "mail" : "li",
          label: `Follow-up #${i + 1}`,
          meta: `${m.channel === "email" ? "Email" : "LinkedIn"} · Day ${m.day}`,
          subject: m.channel === "email" ? m.subject || "" : null,
          body: m.body,
          // Kept so a recorded send says which touch of the sequence it was.
          day: m.day,
        }));

    return raw.map((m, i) => ({
      ...m,
      subject: m.subject === null ? null : val(`${i}:subject`, m.subject),
      body: val(`${i}:body`, m.body),
    }));
  })();

  const cur = msgs[Math.min(sel, Math.max(msgs.length - 1, 0))];

  const curText = cur
    ? (cur.subject ? `Subject: ${cur.subject}\n\n` : "") + cur.body
    : "";

  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(curText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (err) {
      toastr.error("Could not copy. Select the text and copy it by hand.");
    }
  }, [curText]);

  /**
   * Cards whose send has been recorded, by index - for the footer hint. The
   * ref holds exactly what was recorded, so clicking Send twice on the same
   * unchanged text writes one row, while an edited text writes another.
   */
  const [recorded, setRecorded] = useState({});
  const recordedKeys = useRef(new Set());

  /**
   * Store what is being sent. This click is the only point a draft reaches
   * the database - one generated and closed is never written.
   *
   * Fire-and-forget: the send must not wait on PINE, and a failed record must
   * not stop it. The user is told, and can send again to retry.
   */
  const recordSent = () => {
    if (!cur || !String(cur.body || "").trim()) return;

    const channel = cur.ch === "li" ? "linkedin" : "email";
    const key = [sel, channel, cur.subject || "", cur.body].join("\u0000");
    if (recordedKeys.current.has(key)) return;
    recordedKeys.current.add(key);

    const index = sel;

    axios
      .post(
        `${API_BASE_URL}/workstation/record_sent`,
        {
          role_id,
          candidate_id: candidate?.candidate_id,
          kind,
          channel,
          subject: cur.subject,
          body: cur.body,
          day: cur.day ?? null,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then(() => {
        setRecorded((r) => ({ ...r, [index]: true }));
        if (onSent) onSent(kind, channel, new Date().toISOString());
      })
      .catch((err) => {
        recordedKeys.current.delete(key);
        const status = err.response?.status;
        if (status === 401 || status === 403) return;
        console.error("[record_sent] failed:", err.response?.data || err.message);
        toastr.warning(
          "Sent, but PINE could not record it. Send again to retry.",
        );
      });
  };

  /** Everything already sent to this person on this role, newest first. */
  const sentBefore = result?.sent_before || [];

  /**
   * When this card's message was sent, or null. "just now" for a send made
   * in this panel; otherwise the date of a recorded earlier send of the same
   * kind on the same channel - and, for a follow-up, the same day of the
   * sequence.
   */
  const sentFor = (card, index) => {
    if (recorded[index]) return "just now";
    const channel = card.ch === "li" ? "linkedin" : "email";
    const hit = sentBefore.find(
      (s) =>
        s.kind === kind &&
        s.channel === channel &&
        (kind !== "followup" || s.day === card.day),
    );
    return hit ? fmtSentDate(hit.created_at) : null;
  };

  const sendHref = !cur
    ? "#"
    : cur.ch === "mail"
      ? `mailto:${person.email || ""}?subject=${encodeURIComponent(cur.subject || "")}&body=${encodeURIComponent(cur.body)}`
      : person.linkdin_url || "https://www.linkedin.com/messaging/";

  const title = [person.designation, person.company]
    .filter(Boolean)
    .join(" at ");
  const busy = !result && !blocked && !error;

  const NOTE_BOX = {
    marginTop: "16px",
    background: "#fff8ec",
    border: "1px solid #f2d9ae",
    borderRadius: "6px",
    padding: "10px 14px",
    fontSize: "12px",
    color: "#7a5312",
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.55)",
        zIndex: 1050,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
      }}
    >
      <style>{`
        @keyframes pspin{to{transform:rotate(360deg)}}
        @keyframes pfade{from{opacity:0;transform:translateY(6px)}to{opacity:1;transform:none}}
      `}</style>

      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "relative",
          width: "670px",
          maxWidth: "100%",
          maxHeight: "calc(100vh - 48px)",
          overflow: "auto",
          background: "#fff",
          borderRadius: "8px",
          boxShadow: "0 20px 50px rgba(0,0,0,0.3)",
          padding: "24px 26px 30px",
          boxSizing: "border-box",
          animation: "pfade .2s ease-out",
          fontFamily: "Roboto, Helvetica, Arial, sans-serif",
        }}
      >
        <button
          aria-label="Close"
          onClick={onClose}
          style={{
            position: "absolute",
            top: "14px",
            right: "14px",
            width: "22px",
            height: "22px",
            borderRadius: "5px",
            border: `1.5px solid ${ORANGE}`,
            background: "#fff",
            color: "#333",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0,
          }}
        >
          {svg(12, <path d="M18 6 6 18M6 6l12 12" />)}
        </button>

        <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <IconRobot />
          <h2
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 500,
              color: "#1f2937",
            }}
          >
            {cfg.heading}
          </h2>
        </div>

        {/* Same orange and weight as the Source Analysis panel's name line. */}
        <div style={{ marginTop: "6px", fontSize: "13px", color: "#e8692a", fontWeight: 600 }}>
          {person.name}
          {title ? ` — ${title}` : ""}
        </div>

        {busy && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              marginTop: "26px",
            }}
          >
            {cfg.steps.map((text, k) => (
              <div
                key={k}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "13px",
                  color: k <= step ? "#222" : "#6b7280",
                }}
              >
                {k < step && <IconCheck />}
                {k === step && (
                  <span
                    style={{
                      width: "10px",
                      height: "10px",
                      border: "2px solid #f9c3a5",
                      borderTopColor: ORANGE,
                      borderRadius: "50%",
                      animation: "pspin .8s linear infinite",
                      display: "block",
                    }}
                  />
                )}
                {k > step && (
                  <span
                    style={{
                      width: "12px",
                      height: "12px",
                      border: "1.5px solid #c7c9ce",
                      borderRadius: "50%",
                      display: "block",
                    }}
                  />
                )}
                <span>{text}</span>
              </div>
            ))}
          </div>
        )}

        {(blocked || error) && (
          <div
            style={{
              marginTop: "22px",
              background: "#fdf3f2",
              border: "1px solid #f3c9c4",
              borderRadius: "6px",
              padding: "16px",
              fontSize: "13px",
              lineHeight: 1.75,
              color: "#8a2b20",
              animation: "pfade .25s ease-out",
            }}
          >
            {blocked && (
              <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                {blocked.reason === "no_poach"
                  ? "No-poach agreement"
                  : "This person has opted out"}
              </div>
            )}
            {blocked ? blocked.message : error}
          </div>
        )}

        {msgs.length > 0 && (
          <div style={{ animation: "pfade .25s ease-out" }}>
            {result.consent_pending && (
              <div style={NOTE_BOX}>
                This candidate is under GDPR and has not given consent yet.
              </div>
            )}

            {/* Before anyone approaches the same person twice. Information,
                not a block - a new draft is still written below. */}
            {sentBefore.length > 0 && (
              <div
                style={{
                  marginTop: "16px",
                  background: "#eef7f0",
                  border: "1px solid #bfe0c8",
                  borderRadius: "6px",
                  padding: "10px 14px",
                  fontSize: "12px",
                  color: "#1e5b2f",
                  lineHeight: 1.6,
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: "2px" }}>
                  Already sent to {person.name || "this candidate"} on this role
                </div>
                {sentBefore.map((s) => (
                  <div key={s.id}>
                    • {sentLabel(s)} — {fmtSentDate(s.created_at)}
                    {s.by_user ? ` by ${s.by_user}` : ""}
                  </div>
                ))}
              </div>
            )}

            {result.warnings?.length > 0 && (
              <div style={NOTE_BOX}>
                <div style={{ fontWeight: 700, marginBottom: "4px" }}>
                  Read these lines before sending
                </div>
                {result.warnings.map((w, i) => (
                  <div key={i}>• {w}</div>
                ))}
              </div>
            )}

            <div
              style={{
                marginTop: "22px",
                display: "flex",
                flexDirection: "column",
                gap: "12px",
              }}
            >
              {msgs.map((g, k) => {
                const on = k === sel;
                return (
                  <div
                    key={k}
                    role="radio"
                    aria-checked={on}
                    tabIndex={0}
                    onClick={() => {
                      setSel(k);
                      setCopied(false);
                    }}
                    onKeyDown={(e) => {
                      // Only when the card itself has focus. Without this the
                      // card swallows every space and newline typed into the
                      // fields inside it.
                      if (e.target !== e.currentTarget) return;
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setSel(k);
                        setCopied(false);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      border: `1.5px solid ${on ? ORANGE : "#e6e6e8"}`,
                      background: on ? "#fffaf6" : "#f7f7f8",
                      borderRadius: "8px",
                      padding: "14px 16px",
                      boxShadow: on
                        ? "0 0 0 3px rgba(242,107,42,0.12)"
                        : "none",
                      transition:
                        "border-color .15s, box-shadow .15s, background .15s",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <span
                        style={{
                          width: "16px",
                          height: "16px",
                          borderRadius: "50%",
                          border: `1.5px solid ${on ? ORANGE : "#c7c9ce"}`,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxSizing: "border-box",
                        }}
                      >
                        <span
                          style={{
                            width: "8px",
                            height: "8px",
                            borderRadius: "50%",
                            background: on ? ORANGE : "transparent",
                          }}
                        />
                      </span>

                      <span
                        style={{
                          width: "26px",
                          height: "26px",
                          borderRadius: "6px",
                          background: "#fff3ec",
                          color: ORANGE,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {g.ch === "li" ? <IconLinkedIn /> : <IconMail />}
                      </span>

                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          minWidth: 0,
                        }}
                      >
                        <span
                          style={{
                            fontSize: "13px",
                            fontWeight: 700,
                            color: "#222",
                          }}
                        >
                          {g.label}
                        </span>
                        <span style={{ fontSize: "11px", color: "#6b7280" }}>
                          {g.meta}
                        </span>
                      </div>

                      <span
                        style={{
                          marginLeft: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          flexShrink: 0,
                        }}
                      >
                        {sentFor(g, k) && (
                          <span
                            title="Recorded as sent from PINE. Whether it was then delivered, PINE cannot see."
                            style={{
                              fontSize: "11px",
                              fontWeight: 500,
                              color: "#1e7b34",
                              background: "#e6f4ea",
                              borderRadius: "3px",
                              lineHeight: "20px",
                              padding: "0 8px",
                              whiteSpace: "nowrap",
                            }}
                          >
                            ✓ Sent {sentFor(g, k)}
                          </span>
                        )}
                        <span
                          style={{
                            fontSize: "11px",
                            fontWeight: 500,
                            color: ORANGE,
                          }}
                        >
                          {on ? "Selected" : ""}
                        </span>
                      </span>
                    </div>

                    {g.subject !== null && (
                      <div
                        style={{
                          marginTop: "12px",
                          fontSize: "13px",
                          color: "#222",
                          display: "flex",
                          alignItems: "baseline",
                          gap: "6px",
                        }}
                      >
                        <strong style={{ flexShrink: 0 }}>Subject:</strong>
                        <input
                          value={g.subject}
                          onChange={(ev) =>
                            edit(`${k}:subject`, ev.target.value)
                          }
                          style={{ ...FIELD, flex: 1 }}
                        />
                      </div>
                    )}

                    <AutoTextarea
                      value={g.body}
                      onChange={(v) => edit(`${k}:body`, v)}
                      style={{ ...FIELD, marginTop: "10px", lineHeight: 1.7 }}
                    />

                    {g.ch === "li" && (
                      <div
                        style={{
                          marginTop: "6px",
                          fontSize: "11px",
                          color:
                            g.body.length > LINKEDIN_MAX ? "#c0392b" : "#6b7280",
                        }}
                      >
                        {g.body.length} / {LINKEDIN_MAX} characters
                        {g.body.length > LINKEDIN_MAX
                          ? " — too long for a connection request"
                          : ""}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div
              style={{
                display: "flex",
                gap: "8px",
                flexWrap: "wrap",
                alignItems: "center",
                marginTop: "16px",
              }}
            >
              <button
                onClick={copy}
                style={{
                  height: "36px",
                  padding: "0 14px",
                  border: "1px solid #e0e0e0",
                  background: "#fff",
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: 500,
                  fontFamily: "inherit",
                  color: "#222",
                  cursor: "pointer",
                }}
              >
                <IconCopy />
                {copied ? "Copied" : "Copy"}
              </button>

              <a
                href={sendHref}
                target={cur?.ch === "mail" ? undefined : "_blank"}
                rel="noopener noreferrer"
                onClick={() => {
                  // LinkedIn has no link that pre-fills a message, so the text
                  // goes on the clipboard on the way out - the consultant
                  // pastes it into the note without coming back for it.
                  // Email needs none of this: mailto carries the text itself.
                  // Not preventDefault: the profile still opens as before.
                  // Both channels record the send; only LinkedIn needs the
                  // clipboard.
                  recordSent();
                  if (cur?.ch !== "li") return;
                  navigator.clipboard
                    .writeText(curText)
                    .then(() => {
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1800);
                      toastr.success(
                        "Message copied. Paste it into LinkedIn.",
                      );
                    })
                    .catch(() =>
                      toastr.error(
                        "Could not copy the message. Use Copy before sending.",
                      ),
                    );
                }}
                style={{
                  height: "36px",
                  padding: "0 14px",
                  border: "none",
                  background: ORANGE,
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: "#fff",
                  textDecoration: "none",
                }}
              >
                {cur?.ch === "mail" ? <IconMail /> : <IconLinkedIn />}
                {cur?.ch === "mail" ? "Send via Email" : "Send via LinkedIn"}
              </a>

              <span style={{ fontSize: "12px", color: "#6b7280" }}>
                {cur ? `${cur.label} selected` : ""}
                {Object.keys(edits).length > 0 && " · edited"}
                {recorded[sel] && " · recorded as sent"}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
